import { Alert, Button, Field, Modal } from '@catering/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { type FormEvent, type JSX } from 'react';
import { type Resolver, useForm } from 'react-hook-form';

import {
  DEFAULT_DIETARY_TAG_FORM_VALUES,
  type DietaryTagFormFields,
  type DietaryTagFormValues,
  dietaryTagFormSchema,
} from '../../../menus/category-form-schema';

export interface DietaryTagFormProps {
  title: string;
  submitLabel: string;
  mode: 'create' | 'edit';
  defaultValues?: DietaryTagFormFields;
  submitting: boolean;
  formError?: string;
  onCancel: () => void;
  onSubmit: (values: DietaryTagFormValues) => Promise<void>;
}

export function DietaryTagForm({
  title,
  submitLabel,
  mode,
  defaultValues,
  submitting,
  formError,
  onCancel,
  onSubmit,
}: DietaryTagFormProps): JSX.Element {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<DietaryTagFormFields, unknown, DietaryTagFormValues>({
    resolver: zodResolver(dietaryTagFormSchema) as unknown as Resolver<
      DietaryTagFormFields,
      unknown,
      DietaryTagFormValues
    >,
    defaultValues: defaultValues ?? DEFAULT_DIETARY_TAG_FORM_VALUES,
  });

  function handleFormSubmit(event: FormEvent): void {
    void handleSubmit((values) => onSubmit(values))(event);
  }

  return (
    <Modal open onClose={onCancel} title={title} className="max-w-md">
      <form onSubmit={handleFormSubmit} className="flex flex-col gap-4" noValidate>
        {formError !== undefined ? <Alert tone="danger">{formError}</Alert> : null}

        <Field
          label="Código"
          required
          hint="Es interno y no se puede cambiar después. Ejemplo: SIN_TACC"
          disabled={mode === 'edit'}
          {...register('code')}
          {...maybeError(errors.code?.message)}
        />
        <Field
          label="Nombre"
          required
          {...register('name')}
          {...maybeError(errors.name?.message)}
        />
        <Field
          label="Color"
          hint="Opcional. Ejemplo: verde"
          {...register('color')}
          {...maybeError(errors.color?.message)}
        />
        <Field
          label="Ícono"
          hint="Opcional. Ejemplo: leaf"
          {...register('icon')}
          {...maybeError(errors.icon?.message)}
        />
        <Field
          label="Orden de presentación"
          type="number"
          min={0}
          {...register('displayOrder')}
          {...maybeError(errors.displayOrder?.message)}
        />

        <div className="mt-2 flex items-center gap-3">
          <Button type="submit" loading={submitting}>
            {submitLabel}
          </Button>
          <Button type="button" variant="secondary" onClick={onCancel}>
            Cancelar
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function maybeError(message: string | undefined): { error: string } | Record<string, never> {
  return message === undefined ? {} : { error: message };
}
